import { useCallback, useEffect, useRef, useState } from 'react';

import type { DocGrantedUsersService } from '../services/doc-granted-users';

type Page = Awaited<ReturnType<DocGrantedUsersService['fetchReviewPage']>>;
export const PERMISSION_REVIEW_PAGE_SIZE = 24;

/** Each dialog owns its cursor and requests; editing does not reset another consumer. */
export function useDocPermissionReview(
  service: DocGrantedUsersService,
  search: string,
  guestsOnly: boolean | undefined,
  includeCandidates: boolean,
  offline: boolean,
  enabled = true
) {
  const [navigation, setNavigation] = useState<{
    query: string;
    service: DocGrantedUsersService;
    cursors: Array<string | undefined>;
  }>({ service, query: search, cursors: [undefined] });
  const cursors =
    navigation.service === service && navigation.query === search
      ? navigation.cursors
      : [undefined];
  const cursor = cursors.at(-1);
  const [revision, setRevision] = useState(0);
  const [snapshot, setSnapshot] = useState<{
    query: string;
    service: DocGrantedUsersService;
    cursor?: string;
    page: Page;
  }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestId = useRef(0);
  const requestedSearch = useRef<string | undefined>(undefined);
  const currentSearch = useRef(search);
  currentSearch.current = search;
  const searchCandidates = includeCandidates && Boolean(search.trim());
  useEffect(() => {
    const id = ++requestId.current;
    const controller = new AbortController();
    setError(false);
    setLoading(enabled && !offline);
    if (!enabled || offline) return () => controller.abort();
    const debounce = Boolean(search) && requestedSearch.current !== search;
    requestedSearch.current = search;
    // Debounce search only; navigation/revalidation also cancels stale requests.
    const timer = setTimeout(
      () => {
        void service
          .fetchReviewPage(
            { first: PERMISSION_REVIEW_PAGE_SIZE, after: cursor },
            { search, guestsOnly, includeCandidates: searchCandidates },
            AbortSignal.any([controller.signal, AbortSignal.timeout(15_000)])
          )
          .then(page => {
            if (id !== requestId.current || controller.signal.aborted) return;
            setSnapshot({ service, query: search, cursor, page });
          })
          .catch(() => {
            if (!controller.signal.aborted && id === requestId.current)
              setError(true);
          })
          .finally(() => {
            if (!controller.signal.aborted && id === requestId.current)
              setLoading(false);
          });
      },
      debounce ? 250 : 0
    );
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    service,
    search,
    cursor,
    guestsOnly,
    searchCandidates,
    offline,
    enabled,
    revision,
  ]);
  const page =
    snapshot?.service === service &&
    snapshot.query === search &&
    snapshot.cursor === cursor
      ? snapshot.page
      : undefined;
  const reload = useCallback(() => setRevision(value => value + 1), []);
  return {
    page,
    loading,
    error,
    reload,
    start: (cursors.length - 1) * PERMISSION_REVIEW_PAGE_SIZE,
    previous:
      cursors.length > 1
        ? () =>
            setNavigation({
              service,
              query: search,
              cursors: cursors.slice(0, -1),
            })
        : undefined,
    next:
      page?.pageInfo.hasNextPage && page.pageInfo.endCursor
        ? () =>
            setNavigation({
              service,
              query: search,
              cursors: [...cursors, page.pageInfo.endCursor!],
            })
        : undefined,
    first: () => {
      setNavigation({
        service,
        query: currentSearch.current,
        cursors: [undefined],
      });
      reload();
    },
  };
}
